import { create } from 'zustand';
import { createSelectors, withDevtools, StoreName } from '../config';
import { createInitialData } from '../types';
import { EntityName } from 'shared/domain/entityName';
import { levelClient } from 'shared/domain/level/levelClient';
import type { LevelModel } from 'shared/domain/level/types/model';
import { getProjectId } from 'store/project';
import { changeEntityDeleted, createEntityCollectionActions } from '../entityCollection';
import type { LevelsStore, LevelsToSelectSlice } from './types';

export function sortLevels(
  a: { index: number },
  b: { index: number }
): number {
  return a.index - b.index;
}

const initialToSelect: LevelsToSelectSlice = {
  levels: [],
  levelsPerSite: {},
  loading: false,
};

const useLevelsStoreBase = create<LevelsStore>()(
  withDevtools((set, get) => {
    /** Archives or restores at once; undone when the server rejects it. */
    const changeLevelDeleted = (levelId: string, deleted: boolean): void =>
      changeEntityDeleted({
        deleted,
        markDeleted: (value) => get().actions.updateLevelDeleted(levelId, value),
        sendChange: (value) =>
          value ? levelClient.remove(levelId, getProjectId()) : levelClient.restore(levelId, getProjectId()),
        entityLabel: 'a level',
      });

    return {
      data: createInitialData<LevelModel>(),
      isReady: false,
      toSelect: initialToSelect,

      actions: {
        ...createEntityCollectionActions({ client: levelClient, entityName: EntityName.levels }, () => get().actions),
        archive: (levelId) => changeLevelDeleted(levelId, true),
        restore: (levelId) => changeLevelDeleted(levelId, false),

        // Override setItems to always sort levels
        setItems: (items) =>
          set((state) => ({
            ...state,
            data: { ...state.data, items: [...items].sort(sortLevels), loading: false },
            isReady: true,
          })),

        setLoading: (loading) =>
          set((state) => ({
            ...state,
            data: { ...state.data, loading },
          })),

        updateLevelDeleted: (levelId, deleted) =>
          set((state) => ({
            ...state,
            data: {
              ...state.data,
              items: state.data.items.map((level) =>
                level._id === levelId ? { ...level, deleted } : level
              ),
            },
          })),

        setToSelect: (data) =>
          set((state) => ({
            ...state,
            toSelect: { ...state.toSelect, ...data },
          })),

        reset: () =>
          set((state) => ({
            ...state,
            data: createInitialData<LevelModel>(),
            isReady: false,
            toSelect: initialToSelect,
          })),
      },
    };
  }, StoreName.Levels)
);

export const useLevelsStore = createSelectors(useLevelsStoreBase);

export const getLevelsStoreState = (): LevelsStore => useLevelsStore.getState();
